/**
 * Sistema de Animações Centralizado
 * Gerencia animações de surgimento baseadas em Intersection Observer
 */

(function() {
  'use strict';

  // Verificar se o usuário prefere movimento reduzido
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Inicializa animação para um elemento específico
   * @param {string} selector - Seletor CSS do elemento
   * @param {Object} options - Opções de configuração
   * @param {Function} [options.onVisible] - Callback quando elemento fica visível
   * @param {string} [options.animatedClass] - Classe que indica que o elemento está animado
   */
  function initAnimation(selector, options = {}) {
    const element = document.querySelector(selector);
    const animatedClass = options.animatedClass || '--animated';
    
    if (!element || !element.className.includes(animatedClass)) {
      return;
    }

    // Se usuário prefere movimento reduzido, mostrar imediatamente
    if (prefersReducedMotion) {
      element.classList.add('is-visible');
      if (options.onVisible) {
        options.onVisible(element);
      }
      return;
    }

    // Função para verificar se está visível e animar
    function checkAndAnimate() {
      const rect = element.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
      
      if (isVisible) {
        // Se já está visível, animar imediatamente
        element.classList.add('is-visible');
        if (options.onVisible) {
          options.onVisible(element);
        }
        return true;
      }
      return false;
    }

    // Verificar imediatamente
    if (checkAndAnimate()) {
      return;
    }

    // Intersection Observer para detectar quando o elemento está visível
    const observerOptions = {
      root: null,
      rootMargin: '50px',
      threshold: 0.01
    };

    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          if (options.onVisible) {
            options.onVisible(entry.target);
          }
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    observer.observe(element);
  }

  /**
   * Inicializa animação de divider
   * @param {string} selector - Seletor CSS do divider line
   */
  function initDividerAnimation(selector) {
    initAnimation(selector, {
      animatedClass: 'divider__line--animated'
    });
  }

  /**
   * Inicializa animação de typewriter
   * @param {string} selector - Seletor CSS do elemento
   * @param {Object} options - Opções do typewriter
   * @param {number} options.speed - Velocidade em ms entre cada letra
   * @param {number} options.delay - Delay antes de iniciar
   */
  function initTypewriter(selector, options = {}) {
    const element = document.querySelector(selector);
    if (!element) return;

    const speed = options.speed || 30;
    const delay = options.delay || 0;
    const originalText = element.textContent || element.innerText;
    const originalHTML = element.innerHTML;

    // Limpar o conteúdo
    element.innerHTML = '';
    element.style.opacity = '1';

    // Adicionar letras uma por uma
    let currentIndex = 0;
    
    function addNextChar() {
      if (currentIndex >= originalText.length) {
        // Restaurar HTML original
        element.innerHTML = originalHTML;
        element.classList.add('typewriter-complete');
        return;
      }

      const char = originalText[currentIndex];
      const textNode = document.createTextNode(char);
      element.appendChild(textNode);

      currentIndex++;
      setTimeout(addNextChar, speed);
    }

    // Iniciar após o delay
    setTimeout(addNextChar, delay);
  }

  /**
   * Inicializa múltiplas animações de typewriter em uma lista
   * @param {string} selector - Seletor CSS base (deve retornar múltiplos elementos)
   * @param {Object} options - Opções do typewriter
   */
  function initTypewriterList(selector, options = {}) {
    const elements = document.querySelectorAll(selector);
    if (!elements.length) return;

    const speed = options.speed || 30;
    const delay = options.delay || 0;
    const staggerDelay = options.staggerDelay || 200; // Delay entre cada elemento

    elements.forEach((element, index) => {
      const originalText = element.getAttribute('data-original-text') || element.textContent || element.innerText;
      const originalHTML = element.getAttribute('data-original-html') || element.innerHTML;

      // Limpar o conteúdo
      element.innerHTML = '';
      element.style.opacity = '1';

      // Adicionar letras uma por uma
      let currentIndex = 0;
      
      function addNextChar() {
        if (currentIndex >= originalText.length) {
          // Restaurar HTML original
          element.innerHTML = originalHTML;
          element.classList.add('typewriter-complete');
          return;
        }

        const char = originalText[currentIndex];
        const textNode = document.createTextNode(char);
        element.appendChild(textNode);

        currentIndex++;
        setTimeout(addNextChar, speed);
      }

      // Iniciar após o delay + stagger
      setTimeout(addNextChar, delay + (index * staggerDelay));
    });
  }

  /**
   * Inicializa animações para todos os elementos animados em um container
   * Usado quando conteúdo é inserido dinamicamente (ex: filtros, morph)
   * @param {HTMLElement} container - Container onde buscar elementos
   */
  function initInContainer(container) {
    if (!container) return;

    const selector = '[class*="--animated"]:not(.is-visible)';
    const elements = container.querySelectorAll(selector);
    if (elements.length === 0) return;

    elements.forEach(function(element) {
      if (prefersReducedMotion) {
        element.classList.add('is-visible');
        return;
      }

      function checkAndAnimate() {
        const rect = element.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        if (isVisible) {
          element.classList.add('is-visible');
          return true;
        }
        return false;
      }

      if (checkAndAnimate()) return;

      const observerOptions = {
        root: null,
        rootMargin: '50px',
        threshold: 0.01
      };

      const observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      observer.observe(element);
    });
  }

  // Exportar funções para uso global
  window.AnimationSystem = {
    init: initAnimation,
    initInContainer: initInContainer,
    initDivider: initDividerAnimation,
    initTypewriter: initTypewriter,
    initTypewriterList: initTypewriterList
  };

})();

